<script setup>
import { CircleHelp, LogOut, MessageSquare } from 'lucide-vue-next'
import Button from 'primevue/button'
import { useCurrentUser } from '@/composables/useCurrentUser'
import { sessionStore } from '@/stores/session'
import NotificationBell from '@/components/Notifications/NotificationBell.vue'
import { openFeedbackDialog } from '@/composables/feedback'

const { profile, name, initials } = useCurrentUser()
const session = sessionStore()

function logout() {
  session.logout.submit()
}
</script>

<template>
  <header class="mobile-header">
    <RouterLink
      to="/dashboard"
      class="mobile-brand"
    >
      Retiora
    </RouterLink>

    <div class="mobile-header-actions">
      <NotificationBell />
      <RouterLink class="icon-button" :to="{ name: 'getting-started' }" :aria-label="$t('navigation.gettingStarted')">
        <CircleHelp :size="21" />
      </RouterLink>
      <details class="mobile-user-menu">
        <summary class="mobile-avatar">
          <img
            v-if="profile.user_image"
            :src="profile.user_image"
            :alt="name"
          />
          <span v-else>{{ initials }}</span>
        </summary>

        <div class="mobile-user-menu-dropdown">
          <Button
            type="button"
            class="mobile-user-menu-item"
            unstyled
            @click="openFeedbackDialog"
          >
            <MessageSquare :size="17" />
            <span>{{ $t('feedback.sendFeedback') }}</span>
          </Button>
          <Button
            type="button"
            class="mobile-user-menu-item"
            unstyled
            :disabled="session.logout.loading"
            @click="logout"
          >
            <LogOut :size="17" />
            <span>{{ $t('common.logout') }}</span>
          </Button>
        </div>
      </details>
    </div>
  </header>
</template>

<style scoped>
.mobile-header {
  display: none;
}

@media (max-width: 1023px) {
  .mobile-header {
    position: sticky;
    top: 0;
    z-index: 40;
    box-sizing: border-box;
    height: 64px;
    padding: 0 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--surface-card);
    border-bottom: 1px solid #ededf2;
  }

  .mobile-brand {
    color: #4941d1;
    text-decoration: none;
    font-size: 24px;
    font-weight: 750;
  }

  .mobile-header-actions {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 7px;
  }

  .icon-button {
    width: 40px;
    height: 40px;
    display: grid;
    place-items: center;
    padding: 0;
    color: #353646;
    background: transparent;
    border: 0;
    border-radius: 8px;
    cursor: pointer;
  }

  .icon-button:hover {
    background: #f3f3f7;
  }

  .mobile-avatar {
    position: relative;
    width: 30px;
    height: 30px;
    margin-left: 3px;
    overflow: hidden;
    display: grid;
    place-items: center;
    color: #ffffff;
    border-radius: 50%;
    background: #5a55df;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    list-style: none;
  }

  .mobile-avatar::-webkit-details-marker {
    display: none;
  }

  .mobile-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .mobile-user-menu {
    position: relative;
  }

  .mobile-user-menu-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 1;
    min-width: 150px;
    max-width: calc(100vw - 24px);
    padding: 5px;
    background: var(--surface-card);
    border: 1px solid #dedee8;
    border-radius: 9px;
    box-shadow: 0 8px 24px rgb(44 43 93 / 14%);
  }

  .mobile-user-menu-item {
    width: 100%;
    padding: 9px 10px;
    min-height: 40px;
    display: flex;
    align-items: center;
    gap: 9px;
    color: #303142;
    background: transparent;
    border: 0;
    border-radius: 6px;
    font: inherit;
    font-size: 14px;
    text-align: left;
    cursor: pointer;
  }

  .mobile-user-menu-item:hover {
    background: #f5f5fb;
  }

  .mobile-user-menu-item:disabled {
    opacity: 0.6;
    cursor: wait;
  }
}
</style>
